import "./styles.css";
import { ApiClient, ApiError } from "./api";
import {
  formatBytes,
  formatDuration,
  formatEta,
  formatLabel,
  isManifestFormat,
  mediaTypeLabel,
  qualityLabel,
  sortFormats,
} from "./format";
import type {
  ConversionProgress,
  ExtractResponse,
  FormatInfo,
  MediaType,
} from "./types";

function requiredElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error("Missing page element: " + selector);
  return element;
}

const api = new ApiClient();
const form = requiredElement<HTMLFormElement>("#extract-form");
const urlInput = requiredElement<HTMLInputElement>("#video-url");
const submitButton = requiredElement<HTMLButtonElement>("#submit-button");
const result = requiredElement<HTMLElement>("#result");
const loadingResult = requiredElement<HTMLElement>("#loading-result");
const notice = requiredElement<HTMLElement>("#notice");
const noticeMessage = requiredElement<HTMLElement>("#notice-message");
const noticeClose = requiredElement<HTMLButtonElement>("#notice-close");
const formatFilter = requiredElement<HTMLSelectElement>("#format-filter");
const formatList = requiredElement<HTMLTableSectionElement>("#format-list");
const emptyFormats = requiredElement<HTMLElement>("#empty-formats");
const conversionDialog = requiredElement<HTMLDialogElement>("#conversion-dialog");
const dialogClose = requiredElement<HTMLButtonElement>("#dialog-close");
const progressTrack = requiredElement<HTMLElement>("#conversion-progress");
const progressBar = requiredElement<HTMLElement>("#conversion-progress-bar");
const conversionTitle = requiredElement<HTMLElement>("#conversion-title");
const conversionMessage = requiredElement<HTMLElement>("#conversion-message");
const conversionPhase = requiredElement<HTMLElement>("#conversion-phase");
const conversionDetails = requiredElement<HTMLElement>("#conversion-details");
const conversionPercent = requiredElement<HTMLElement>("#conversion-percent");
const conversionElapsed = requiredElement<HTMLElement>("#conversion-elapsed");

let formats: FormatInfo[] = [];
let activeExtraction: AbortController | null = null;
let conversionRunning = false;
let conversionTimer: number | undefined;
let elapsedTimer: number | undefined;

const sharedUrlPrefix = "/v/";

function encodeSharedUrl(url: string): string {
  const bytes = new TextEncoder().encode(url);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function decodeSharedUrl(encodedUrl: string): string {
  if (!/^[A-Za-z0-9_-]+$/.test(encodedUrl) || encodedUrl.length > 8192) {
    throw new Error("This shared video URL is invalid.");
  }
  try {
    const base64 = encodedUrl
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(encodedUrl.length / 4) * 4, "=");
    const binary = atob(base64);
    const bytes = Uint8Array.from(binary, (character) =>
      character.charCodeAt(0),
    );
    const url = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    if (validVideoUrl(url)) return url;
  } catch {
    // All malformed Base64 and UTF-8 inputs receive the same safe message.
  }
  throw new Error("This shared video URL is invalid.");
}

function sharedUrlFromLocation(): string | null {
  if (!window.location.pathname.startsWith(sharedUrlPrefix)) return null;
  const encodedUrl = window.location.pathname.slice(sharedUrlPrefix.length);
  if (!encodedUrl || encodedUrl.includes("/")) {
    throw new Error("This shared video URL is invalid.");
  }
  return decodeSharedUrl(encodedUrl);
}

function updateSharedLocation(url: string): void {
  const path = sharedUrlPrefix + encodeSharedUrl(url);
  if (window.location.pathname !== path || window.location.search) {
    window.history.pushState({ videoUrl: url }, "", path);
  }
}

function setLoading(loading: boolean): void {
  submitButton.disabled = loading;
  submitButton.classList.toggle("is-loading", loading);
  submitButton.querySelector<HTMLElement>(".button-label")!.textContent = loading
    ? "Checking"
    : "Find formats";
  loadingResult.hidden = !loading;
  if (loading) result.hidden = true;
}

function showNotice(message: string, kind: "error" | "info" = "error"): void {
  noticeMessage.textContent = message;
  notice.dataset.kind = kind;
  notice.hidden = false;
  notice.scrollIntoView({ behavior: "smooth", block: "nearest" });
}

function hideNotice(): void {
  notice.hidden = true;
  noticeMessage.textContent = "";
}

function validVideoUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function createCell(label: string, value: string): HTMLTableCellElement {
  const cell = document.createElement("td");
  cell.dataset.label = label;
  cell.textContent = value;
  return cell;
}

function addAction(
  container: HTMLElement,
  label: string,
  className: string,
  href: string,
  onClick?: (event: MouseEvent) => void,
): void {
  const link = document.createElement("a");
  link.className = "download-action " + className;
  link.href = api.resolve(href);
  link.textContent = label;
  if (onClick) {
    link.addEventListener("click", onClick);
  } else {
    link.target = "_blank";
    link.rel = "noopener noreferrer nofollow";
  }
  container.append(link);
}

function createFormatRow(format: FormatInfo): HTMLTableRowElement {
  const row = document.createElement("tr");

  const qualityCell = document.createElement("td");
  qualityCell.dataset.label = "Quality";
  const quality = document.createElement("div");
  quality.className = "quality-value";
  quality.textContent = qualityLabel(format);
  const formatId = document.createElement("span");
  formatId.className = "format-id";
  formatId.textContent = "ID " + format.format_id;
  qualityCell.append(quality, formatId);

  const typeCell = document.createElement("td");
  typeCell.dataset.label = "Type";
  const typePill = document.createElement("span");
  typePill.className = "type-pill type-" + format.media_type.replace("+", "-");
  typePill.textContent = mediaTypeLabel(format.media_type);
  typeCell.append(typePill);

  const actionsCell = document.createElement("td");
  actionsCell.className = "actions-cell";
  actionsCell.dataset.label = "Download";
  const actions = document.createElement("div");
  actions.className = "action-group";

  if (format.server_download_url) {
    addAction(
      actions,
      "Prepare MP4",
      "primary-action",
      format.server_download_url,
      (event) => {
        event.preventDefault();
        void prepareDownload(format.server_download_url!);
      },
    );
  }

  if (!isManifestFormat(format) || !format.server_download_url) {
    addAction(
      actions,
      format.server_download_url ? "Direct download" : "Download",
      "secondary-action",
      format.url,
    );
  }

  actionsCell.append(actions);
  row.append(
    qualityCell,
    typeCell,
    createCell("Format", formatLabel(format)),
    createCell("Size", formatBytes(format.file_size)),
    actionsCell,
  );
  return row;
}

function renderFormats(): void {
  const selected = formatFilter.value as MediaType | "all";
  const visible =
    selected === "all"
      ? formats
      : formats.filter((format) => format.media_type === selected);

  formatList.replaceChildren(...visible.map(createFormatRow));
  emptyFormats.hidden = visible.length > 0;
  requiredElement<HTMLElement>("#format-count").textContent =
    visible.length +
    (visible.length === 1 ? " format shown" : " formats shown") +
    (visible.length !== formats.length ? " of " + formats.length : "");
}

function renderResult(data: ExtractResponse): void {
  formats = sortFormats(data.formats);

  const thumbnail = requiredElement<HTMLImageElement>("#thumbnail");
  const fallback = requiredElement<HTMLElement>("#thumbnail-fallback");
  if (data.thumbnail && /^https?:\/\//i.test(data.thumbnail)) {
    thumbnail.src = data.thumbnail;
    thumbnail.alt = "Thumbnail for " + data.title;
    thumbnail.hidden = false;
    fallback.hidden = true;
  } else {
    thumbnail.removeAttribute("src");
    thumbnail.hidden = true;
    fallback.hidden = false;
  }

  const durationBadge = requiredElement<HTMLElement>("#duration-badge");
  const duration = formatDuration(data.duration_seconds);
  durationBadge.textContent = duration;
  durationBadge.hidden = !duration;

  requiredElement<HTMLElement>("#extractor-label").textContent =
    data.extractor || "Video";
  requiredElement<HTMLElement>("#quality-count").textContent =
    new Set(formats.map((format) => format.height).filter(Boolean)).size +
    " qualities";
  requiredElement<HTMLElement>("#video-title").textContent = data.title;
  requiredElement<HTMLElement>("#format-total").textContent =
    formats.length + (formats.length === 1 ? " option" : " options");

  const uploaderMetadata = requiredElement<HTMLElement>("#uploader-metadata");
  requiredElement<HTMLElement>("#uploader").textContent = data.uploader || "";
  uploaderMetadata.hidden = !data.uploader;

  const sourceLink = requiredElement<HTMLAnchorElement>("#source-link");
  sourceLink.href = data.webpage_url;

  formatFilter.value = "all";
  renderFormats();
  result.hidden = false;
  result.scrollIntoView({ behavior: "smooth", block: "start" });
}

function replaceDownloadPath(path: string, replacement: string): string {
  return path.replace("/api/v1/download/", replacement);
}

function resetDialog(): void {
  conversionRunning = true;
  dialogClose.hidden = true;
  conversionTitle.textContent = "Getting your MP4 ready";
  conversionMessage.textContent =
    "Downloading the selected stream and merging audio when needed.";
  conversionPhase.textContent = "Waiting for converter";
  conversionDetails.textContent = "Progress will appear shortly";
  conversionPercent.textContent = "—";
  conversionElapsed.textContent = "Starting…";
  progressTrack.classList.add("indeterminate");
  progressTrack.removeAttribute("aria-valuenow");
  progressBar.style.removeProperty("width");

  const startedAt = Date.now();
  window.clearInterval(elapsedTimer);
  elapsedTimer = window.setInterval(() => {
    const seconds = Math.floor((Date.now() - startedAt) / 1000);
    conversionElapsed.textContent =
      "Working for " + seconds + (seconds === 1 ? " second" : " seconds");
  }, 1000);
}

function renderProgress(progress: ConversionProgress): void {
  const phaseNames: Record<ConversionProgress["phase"], string> = {
    queued: "Waiting for converter",
    starting: "Starting secure download",
    downloading: "Downloading media",
    processing: "Merging video and audio",
    ready: "Download ready",
    failed: "Preparation failed",
  };
  conversionPhase.textContent = phaseNames[progress.phase];

  if (Number.isFinite(progress.percent)) {
    const percent = Math.max(0, Math.min(100, progress.percent!));
    progressTrack.classList.remove("indeterminate");
    progressTrack.setAttribute("aria-valuenow", String(Math.round(percent)));
    progressBar.style.width = percent + "%";
    conversionPercent.textContent = Math.round(percent) + "%";
  } else {
    progressTrack.classList.add("indeterminate");
    progressTrack.removeAttribute("aria-valuenow");
    progressBar.style.removeProperty("width");
    conversionPercent.textContent = "—";
  }

  const details: string[] = [];
  if (Number.isFinite(progress.downloaded_bytes)) {
    let amount = formatBytes(progress.downloaded_bytes);
    if (Number.isFinite(progress.total_bytes)) {
      amount += " of " + formatBytes(progress.total_bytes);
    }
    details.push(amount);
  }
  if (Number.isFinite(progress.speed_bytes_per_second)) {
    details.push(formatBytes(progress.speed_bytes_per_second) + "/s");
  }
  const eta = formatEta(progress.eta_seconds);
  if (eta) details.push(eta);
  conversionDetails.textContent =
    progress.message ||
    details.join(" · ") ||
    (progress.phase === "processing"
      ? "Finalizing your MP4 file"
      : "Progress will appear shortly");
}

async function pollProgress(statusUrl: string): Promise<void> {
  if (!conversionRunning) return;
  try {
    const progress = await api.progress(statusUrl);
    renderProgress(progress);
  } catch {
    // Preparation remains authoritative; transient polling errors are harmless.
  }
  if (conversionRunning) {
    conversionTimer = window.setTimeout(() => {
      void pollProgress(statusUrl);
    }, 800);
  }
}

function finishDialog(success: boolean, message: string): void {
  conversionRunning = false;
  window.clearTimeout(conversionTimer);
  window.clearInterval(elapsedTimer);
  dialogClose.hidden = false;
  conversionMessage.textContent = message;

  if (success) {
    progressTrack.classList.remove("indeterminate");
    progressTrack.setAttribute("aria-valuenow", "100");
    progressBar.style.width = "100%";
    conversionPercent.textContent = "100%";
    conversionPhase.textContent = "Download ready";
    conversionDetails.textContent = "Your browser download is starting";
    conversionElapsed.textContent = "Preparation complete";
  } else {
    conversionTitle.textContent = "We couldn't prepare this file";
    conversionPhase.textContent = "Preparation stopped";
    conversionDetails.textContent = "Close this dialog and try another format";
    conversionElapsed.textContent = "";
  }
}

async function prepareDownload(downloadUrl: string): Promise<void> {
  resetDialog();
  if (!conversionDialog.open) conversionDialog.showModal();

  const prepareUrl = replaceDownloadPath(downloadUrl, "/api/v1/prepare/");
  const statusUrl = replaceDownloadPath(
    downloadUrl,
    "/api/v1/conversion-status/",
  );
  void pollProgress(statusUrl);

  try {
    const prepared = await api.prepare(prepareUrl);
    finishDialog(true, "Your MP4 is ready. The download should begin automatically.");
    window.location.assign(api.resolve(prepared.download_url));
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "The file could not be prepared.";
    finishDialog(false, message);
  }
}

async function extractVideo(
  url: string,
  updateLocation = true,
): Promise<void> {
  hideNotice();

  if (!validVideoUrl(url)) {
    showNotice("Enter a complete video page URL beginning with http:// or https://.");
    urlInput.focus();
    return;
  }

  activeExtraction?.abort();
  const controller = new AbortController();
  activeExtraction = controller;
  setLoading(true);

  if (updateLocation) updateSharedLocation(url);

  try {
    const data = await api.extract(url, controller.signal);
    renderResult(data);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return;
    const message =
      error instanceof ApiError || error instanceof Error
        ? error.message
        : "The video information could not be extracted.";
    showNotice(message);
  } finally {
    if (activeExtraction === controller) {
      activeExtraction = null;
      setLoading(false);
    }
  }
}

form.addEventListener("submit", (event) => {
  event.preventDefault();
  void extractVideo(urlInput.value.trim());
});

async function loadSharedLocation(): Promise<void> {
  try {
    const url = sharedUrlFromLocation();
    if (url === null) {
      activeExtraction?.abort();
      activeExtraction = null;
      formats = [];
      urlInput.value = "";
      result.hidden = true;
      setLoading(false);
      hideNotice();
      return;
    }

    urlInput.value = url;
    await extractVideo(url, false);
  } catch (error) {
    activeExtraction?.abort();
    activeExtraction = null;
    result.hidden = true;
    setLoading(false);
    showNotice(
      error instanceof Error
        ? error.message
        : "This shared video URL is invalid.",
    );
  }
}

window.addEventListener("popstate", () => {
  void loadSharedLocation();
});
formatFilter.addEventListener("change", renderFormats);
noticeClose.addEventListener("click", hideNotice);
dialogClose.addEventListener("click", () => conversionDialog.close());
conversionDialog.addEventListener("cancel", (event) => {
  if (conversionRunning) event.preventDefault();
});

requiredElement<HTMLElement>("#current-year").textContent = String(
  new Date().getFullYear(),
);

void loadSharedLocation();
